import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import DataTable from 'react-data-table-component';
import {
    FiPlus, FiEdit2, FiTrash2, FiClock, FiCalendar,
    FiX, FiSearch, FiRefreshCw
} from 'react-icons/fi';
import TicketSchedulerViewModal from './TicketSchedulerViewModal';

/* ─────────── Custom Pagination (identical to TicketTable) ─────────── */
const CustomPagination = ({ currentPage, rowCount, rowsPerPage, onChangePage }) => {
    if (!rowCount || rowCount === 0) return null;
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, rowCount);

    const getPageNumbers = () => {
        const isMobile = window.innerWidth < 640;
        const maxVisible = isMobile ? 3 : 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }
        startPage = Math.max(1, startPage);
        const pages = [];
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="text-sm text-gray-600 order-2 sm:order-1">
                Showing <span className="font-semibold">{start}-{end}</span> of <span className="font-semibold">{rowCount}</span>
            </div>
            <div className="flex gap-1 sm:gap-2 items-center order-1 sm:order-2">
                <button
                    type="button"
                    onClick={() => onChangePage(1)}
                    disabled={currentPage <= 1}
                    className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
                >
                    <span className="hidden sm:inline">First</span>
                    <span className="sm:hidden text-lg">«</span>
                </button>
                {getPageNumbers().map((pageNum) => (
                    <button
                        key={pageNum}
                        type="button"
                        onClick={() => onChangePage(pageNum)}
                        className={`min-w-[32px] h-[32px] flex items-center justify-center text-xs sm:text-sm border rounded font-medium transition-all ${currentPage === pageNum
                            ? 'bg-red-600 text-white border-red-600 shadow-sm scale-110 z-10'
                            : 'border-gray-300 hover:bg-gray-100 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                    >
                        {pageNum}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => onChangePage(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
                >
                    <span className="hidden sm:inline">Last</span>
                    <span className="sm:hidden text-lg">»</span>
                </button>
            </div>
        </div>
    );
};

/* ─────────── Delete Confirmation Popup (Simple & Compact) ─────────── */
const DeleteConfirmPopup = ({ onConfirm, onCancel, title }) => (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/30 px-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-xs border border-gray-200 overflow-hidden">
            <div className="px-5 pt-5 pb-4 text-center">
                <div className="inline-flex items-center justify-center w-9 h-9 bg-red-50 rounded-full mb-3">
                    <FiTrash2 className="text-red-500" size={16} />
                </div>
                <p className="text-gray-800 font-semibold text-sm">Delete this schedule?</p>
                <p className="text-gray-500 text-xs mt-1 truncate max-w-[220px] mx-auto" title={title}>
                    "{title}"
                </p>
            </div>
            <div className="px-5 pb-4 flex gap-2">
                <button
                    onClick={onCancel}
                    className="flex-1 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-all"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className="flex-1 py-1.5 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-600 transition-all"
                >
                    Delete
                </button>
            </div>
        </div>
    </div>
);

/* ─────────── Toggle Switch ─────────── */
const ToggleSwitch = ({ isActive, onToggle }) => (
    <div className="flex flex-col items-center gap-0.5">
        <button
            onClick={onToggle}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-300 focus:outline-none shadow-inner ${isActive ? 'bg-green-500' : 'bg-gray-300'
                }`}
        >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
        </button>
        <span className={`text-[9px] font-bold ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
            {isActive ? 'Active' : 'Inactive'}
        </span>
    </div>
);

/* ─────────── Custom Styles (identical to TicketTable) ─────────── */
const customStyles = {
    headRow: {
        style: {
            backgroundColor: '#f3f4f6',
            color: '#374151',
            fontWeight: '600',
            fontSize: '0.75rem',
            borderBottom: '2px solid #d1d5db',
            minHeight: '45px',
        },
    },
    headCells: {
        style: {
            paddingLeft: '12px',
            paddingRight: '12px',
            textAlign: 'center',
            fontSize: '0.75rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            whiteSpace: 'normal',
            overflow: 'visible',
            wordBreak: 'break-word',
        },
    },
    cells: {
        style: {
            paddingLeft: '12px',
            paddingRight: '12px',
            fontSize: '0.75rem',
            paddingTop: '12px',
            paddingBottom: '12px',
            textAlign: 'center',
            verticalAlign: 'middle',
        },
    },
    rows: {
        style: {
            minHeight: '60px',
            borderBottom: '1px solid #e5e7eb',
            '&:hover': { backgroundColor: '#fef2f2' },
            '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
        },
    },
    table: { style: { minWidth: '1050px' } },
    tableWrapper: { style: { overflowX: 'auto', width: '100%' } },
};

const tableCSS = `
.rdt_Pagination { padding: 0 !important; margin: 0 !important; min-height: 0 !important; }
.rdt_TableRow { user-select: none; }
.rdt_TableHeadRow .rdt_TableCol > div { text-align: center !important; justify-content: center; }
.rdt_TableBody .rdt_TableCell { text-align: center !important; }
.rdt_TableBody .rdt_TableCell > div { text-align: center !important; margin: 0 auto; }

/* Left-align Title column cells */
.rdt_TableBody .rdt_TableCell:has(.scheduler-title-cell) {
    text-align: left !important;
}
.rdt_TableBody .rdt_TableCell:has(.scheduler-title-cell) > div {
    text-align: left !important;
    margin: 0 !important;
    justify-content: flex-start !important;
}

/* Prevent cell content from swallowing clicks so row click works */
.rdt_TableBody .rdt_TableRow .rdt_TableCell > * { pointer-events: none; }
/* EXCEPTions for interactive elements so they remain clickable */
.rdt_TableBody .rdt_TableCell button, 
.rdt_TableBody .rdt_TableCell a,
.rdt_TableBody .rdt_TableCell .status-toggle,
.rdt_TableBody .rdt_TableCell input { 
    pointer-events: auto !important; 
}
/* Prevent header text from being cut off */
.rdt_TableCol_Sortable, .rdt_TableCol > div { white-space: normal !important; overflow: visible !important; word-break: break-word; }
.rdt_TableHeadRow .rdt_TableCol { overflow: visible !important; }
`;

/* ─────────── Main Component ─────────── */
const TicketSchedulerTable = ({ onEdit }) => {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const saved = localStorage.getItem('schedulerRowsPerPage');
        return saved ? parseInt(saved, 10) : 5;
    });
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterModule, setFilterModule] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [viewIndex, setViewIndex] = useState(-1);


    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const response = await api.get('/ticket-scheduler');
            setSchedules(response.data);
        } catch {
            toast.error('Failed to load schedules');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSchedules(); }, []);

    useEffect(() => {
        localStorage.setItem('schedulerRowsPerPage', String(rowsPerPage));
    }, [rowsPerPage]);

    /* dropdown unique values */
    const uniqueCustomers = useMemo(() => {
        const seen = new Set();
        return schedules.filter(r => {
            const v = r.client_short_code || r.client_display_name;
            if (!v || seen.has(v)) return false;
            seen.add(v); return true;
        });
    }, [schedules]);

    const uniqueModules = useMemo(() => {
        const seen = new Set();
        return schedules.filter(r => {
            const v = r.module_display_name;
            if (!v || seen.has(v)) return false;
            seen.add(v); return true;
        });
    }, [schedules]);

    /* toggle */
    const handleToggleStatus = async (id, currentStatus) => {
        const newStatus = Number(currentStatus) === 1 ? 0 : 1;
        try {
            await api.post(`/ticket-scheduler/toggle/${id}`, { is_active: newStatus });
            toast.success(`Schedule ${newStatus === 1 ? 'activated' : 'deactivated'}`);
            fetchSchedules();
        } catch {
            toast.error('Failed to update status');
        }
    };

    /* delete */
    const handleDeleteConfirmed = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/ticket-scheduler/delete/${deleteTarget.id}`);
            toast.success('Schedule deleted');
            setDeleteTarget(null);
            fetchSchedules();
        } catch {
            toast.error('Failed to delete schedule');
            setDeleteTarget(null);
        }
    };

    /* recurrence text */
    const getRecurrenceText = useCallback((row) => {
        const interval = row.repeat_interval > 1 ? `Every ${row.repeat_interval} ` : '';
        const timeText = row.start_time ? ` at ${row.start_time}` : '';
        if (row.daily) return `${interval}Daily${timeText}`;
        if (row.weekly) return `${interval}Weekly${timeText}`;
        if (row.monthly) return `${interval}Monthly${timeText}`;
        if (row.yearly) return `${interval}Yearly${timeText}`;
        return 'N/A';
    }, []);

    /* filtered data */
    const filteredData = useMemo(() => {
        let list = schedules;
        const q = searchText.toLowerCase().trim();
        if (q) {
            list = list.filter(r =>
                (r.title || '').toLowerCase().includes(q) ||
                (r.client_short_code || r.client_display_name || '').toLowerCase().includes(q) ||
                (r.module_display_name || '').toLowerCase().includes(q) ||
                (r.issue_type || '').toLowerCase().includes(q) ||
                (r.assigned_names || '').toLowerCase().includes(q)
            );
        }
        if (filterCustomer) {
            list = list.filter(r =>
                (r.client_short_code || r.client_display_name || '') === filterCustomer
            );
        }
        if (filterModule) {
            list = list.filter(r => (r.module_display_name || '') === filterModule);
        }
        return list;
    }, [schedules, searchText, filterCustomer, filterModule]);
 
    const viewTarget = viewIndex >= 0 ? filteredData[viewIndex] : null;

    /* columns */
    const H = ({ children }) => (
        <span style={{ whiteSpace: 'nowrap', overflow: 'visible', display: 'block', textAlign: 'center', width: '100%' }}>
            {children}
        </span>
    );

    const columns = useMemo(() => [
        {
            name: <H>ID</H>,
            selector: row => Number(row.schedule_id) || 0,
            sortable: true,
            width: '65px',
            compact: true,
            center: true,
            cell: row => <div className="font-bold text-gray-800 text-xs">#{row.schedule_id}</div>,
        },
        {
            name: <H>Title</H>,
            selector: row => row.title || '',
            sortable: true,
            minWidth: '150px',
            cell: row => (
                <div className="text-xs font-semibold text-gray-800 text-left break-words w-full scheduler-title-cell" title={row.title}>
                    {row.title}
                </div>
            ),
        },
        {
            name: <H>Customer</H>,
            selector: row => row.client_short_code || row.client_display_name || '',
            sortable: true,
            width: '105px',
            cell: row => (
                <span className="font-bold uppercase text-gray-800 text-xs">
                    {row.client_short_code || row.client_display_name || 'N/A'}
                </span>
            ),
        },
        {
            name: <H>Module</H>,
            selector: row => row.module_display_name || '',
            sortable: true,
            minWidth: '115px',
            cell: row => (
                <div className="text-center">
                    <div className="font-medium text-gray-800 text-xs">{row.module_display_name || '-'}</div>
                </div>
            ),
        },
        {
            name: <H>Issue Type</H>,
            selector: row => row.issue_type || '',
            sortable: true,
            width: '100px',
            cell: row => <div className="text-xs">{row.issue_type || '-'}</div>,
        },
        {
            name: <H>Recurrence</H>,
            selector: row => getRecurrenceText(row),
            sortable: true,
            minWidth: '160px',
            cell: row => (
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium border border-indigo-100 whitespace-nowrap">
                    <FiCalendar size={10} />
                    {getRecurrenceText(row)}
                </div>
            ),
        },
        {
            name: <H>Assigned To</H>,
            selector: row => row.assigned_names || '',
            sortable: true,
            minWidth: '135px',
            cell: row => (
                <div className="whitespace-normal text-center text-xs">
                    {(row.assigned_names || 'Unassigned').replace(/,/g, '\n')}
                </div>
            ),
        },
        {
            name: <H>Last Run</H>,
            selector: row => row.last_created_date || '',
            sortable: true,
            minWidth: '145px',
            cell: row => (
                <div className="text-xs text-gray-500 whitespace-nowrap">
                    {row.last_created_date || '-'}
                </div>
            ),
        },
        {
            name: <H>Status</H>,
            selector: row => Number(row.is_active),
            sortable: true,
            width: '85px',
            cell: row => (
                <ToggleSwitch
                    isActive={Number(row.is_active) === 1}
                    onToggle={() => handleToggleStatus(row.schedule_id, row.is_active)}
                />
            ),
        },
        {
            name: <H>Actions</H>,
            width: '85px',
            center: true,
            cell: row => (
                <div className="flex items-center justify-center gap-1.5 transition-all">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-all border border-blue-100 bg-white"
                        title="Edit"
                    >
                        <FiEdit2 size={13} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: row.schedule_id, title: row.title }); }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-all border border-red-100 bg-white"
                        title="Delete"
                    >
                        <FiTrash2 size={13} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ], [getRecurrenceText, onEdit]);

    return (
        <>
            <style>{tableCSS}</style>

            {deleteTarget && (
                <DeleteConfirmPopup
                    title={deleteTarget.title}
                    onConfirm={handleDeleteConfirmed}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}

            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">

                {viewTarget && (
                    <TicketSchedulerViewModal
                        schedule={viewTarget}
                        onClose={() => setViewIndex(-1)}
                        onEdit={(row) => {
                            setViewIndex(-1);
                            onEdit(row);
                        }}
                        onNext={() => setViewIndex(prev => Math.min(filteredData.length - 1, prev + 1))}
                        onPrevious={() => setViewIndex(prev => Math.max(0, prev - 1))}
                        hasNext={viewIndex < filteredData.length - 1}
                        hasPrevious={viewIndex > 0}
                    />
                )}

                {/* ── Top Bar: header + Create button ── */}
                <div className="px-4 sm:px-6 pt-4 pb-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">
                            <FiClock className="text-red-600" />
                            Ticket Scheduler
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">Manage recurring maintenance and automation tickets</p>
                    </div>
                    <button
                        onClick={() => onEdit(null)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all shadow-md active:scale-95 whitespace-nowrap self-start sm:self-auto"
                    >
                        <FiPlus /> Create Schedule
                    </button>
                </div>

                {/* ── Filter Bar (identical layout to TicketListPage) ── */}
                <div className="px-4 sm:px-6 py-3 flex flex-wrap gap-3 items-end border-b border-gray-100 bg-gray-50/50">

                    {/* Rows per page */}
                    <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Rows Per Page</label>
                        <select
                            value={rowsPerPage}
                            onChange={e => setRowsPerPage(Number(e.target.value))}
                            className="h-[30px] pl-2 pr-6 text-xs border border-gray-300 rounded bg-white outline-none"
                        >
                            {[5, 10, 25, 50].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                    {/* Customer filter */}
                    <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Customer</label>
                        <select
                            value={filterCustomer}
                            onChange={e => setFilterCustomer(e.target.value)}
                            className="h-[30px] pl-2 pr-6 text-xs border border-gray-300 rounded bg-white outline-none min-w-[100px]"
                        >
                            <option value="">All</option>
                            {uniqueCustomers.map((r, i) => (
                                <option key={i} value={r.client_short_code || r.client_display_name}>
                                    {r.client_short_code || r.client_display_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Module filter */}
                    <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Module</label>
                        <select
                            value={filterModule}
                            onChange={e => setFilterModule(e.target.value)}
                            className="h-[30px] pl-2 pr-6 text-xs border border-gray-300 rounded bg-white outline-none min-w-[130px]"
                        >
                            <option value="">All Modules</option>
                            {uniqueModules.map((r, i) => (
                                <option key={i} value={r.module_display_name}>
                                    {r.module_display_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Search */}
                    <div className="flex flex-col gap-0.5 w-full sm:w-48">
                        <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Search</label>
                        <div className="relative">
                            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                            <input
                                type="text"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                placeholder="ID, Title, Status..."
                                className="w-full pl-7 pr-6 h-[30px] text-xs border border-gray-300 rounded bg-white outline-none focus:border-red-400"
                            />
                            {searchText && (
                                <button
                                    onClick={() => setSearchText('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <FiX size={11} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Refresh */}
                    <button
                        onClick={fetchSchedules}
                        className="h-[30px] px-3 flex items-center gap-1.5 text-xs text-gray-600 border border-gray-300 rounded bg-white hover:bg-gray-50 transition-all whitespace-nowrap"
                        title="Refresh"
                    >
                        <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                        <span className="hidden sm:inline">Refresh</span>
                    </button>
                </div>

                {/* ── DataTable ── */}
                <div className="overflow-x-auto relative min-h-[300px]">
                    <DataTable
                        columns={columns}
                        data={filteredData}
                        onRowClicked={(row) => {
                            const idx = filteredData.findIndex(r => r.schedule_id === row.schedule_id);
                            setViewIndex(idx);
                        }}
                        pointerOnHover
                        progressPending={loading}
                        progressComponent={
                            <div className="flex flex-col items-center justify-center py-16 min-h-[300px]">
                                <div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mb-3" />
                                <span className="text-sm text-gray-400 font-medium">Loading schedules...</span>
                            </div>
                        }
                        persistTableHead
                        customStyles={customStyles}
                        highlightOnHover
                        noDataComponent={
                            <div className="flex justify-center items-center h-40 text-base text-gray-500 italic py-8">
                                No schedules found. Create one to get started.
                            </div>
                        }
                        pagination
                        paginationTotalRows={filteredData.length}
                        paginationPerPage={rowsPerPage}
                        paginationDefaultPage={1}
                        paginationComponent={CustomPagination}
                        paginationRowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </div>
            </div>
        </>
    );
};

export default TicketSchedulerTable;
